import * as React from 'react'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import { Panes } from 'modules/core/components/Panes'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import CustomFields from 'modules/custom_resources/components/CustomFields'
import { FieldTypes } from 'modules/custom_resources/store/collections/customFields/fields'
import ResourceFieldsCollection from 'modules/custom_resources/store/collections/customFields/resourceFields'
import FieldsCollection from 'modules/custom_resources/store/collections/customResources/fields'
import type Field from 'modules/custom_resources/store/models/customFields/Field'
import type CustomField from 'modules/custom_resources/store/models/customFields/Field'
import Schema from 'modules/custom_resources/store/models/customResources/Schema'

import Details from './_Details'

function customFieldsActions(schema: Schema) {
  const isEmployeesTable = schema.attributes.topic === Schema.TOPIC_NAMES.employees

  const isPerformanceTable = schema.attributes.topic === Schema.TOPIC_NAMES.performance

  const employeesFeatureEnabled = hasFeature('employees_custom_tables')

  const anyPerformanceFeatureEnabled =
    hasFeature('trainings_custom_table') ||
    hasFeature('competencies_custom_table') ||
    hasFeature('growth_plans_custom_table')

  /*
    A user can create custom fields of a custom table only
    if the custom tables feature is active and the table is created by an employee
  */
  const canCreate = isEmployeesTable && employeesFeatureEnabled
  /*
    We are allowing the deletion of custom fields only if the custom tables feature is enabled (which is the case for companies
    with the deprecated apps installed))
  */
  const canDelete =
    (employeesFeatureEnabled && isEmployeesTable) ||
    (anyPerformanceFeatureEnabled && isPerformanceTable)

  /*
    A user can edit a custom field if at least one of the featues for custom tables is enabled
    The trainings_custom_table, competencies_custom_table and growth_plans_custom_table are enabled
    when the feature installed, ussually through the app store
  */
  const canEdit =
    (employeesFeatureEnabled && isEmployeesTable) ||
    (anyPerformanceFeatureEnabled && isPerformanceTable)

  return {
    canCreate,
    canDelete,
    canEdit,
  }
}

interface Props {
  schema: Schema
  updateSchema: (name: string) => Promise<unknown>
  canManageResourceCustomFields: boolean
}

const Content: React.FC<Props> = ({ schema, updateSchema, canManageResourceCustomFields }) => {
  const { navigation } = useNavigation()

  const DescriptionLink = {
    href: navigation.settings.permissions.fullPath(),
    label: i18n.t('settings.custom_resources.description_link'),
  }

  const id = schema.attributes.id
  const fields = React.useRef(new FieldsCollection()).current

  const resourceFields = React.useRef(
    new ResourceFieldsCollection([], {
      model_ref: 'custom_resources-schema',
      instance_id: String(id),
    })
  ).current

  const { loading } = useAsyncData(
    () =>
      Promise.all([
        fields.fetch({
          data: {
            schema_ids: [id],
          },
        }),
        resourceFields.fetch(),
      ]),
    [id]
  )

  const { canCreate, canEdit, canDelete } = customFieldsActions(schema)

  const createCustomField = async (data: any) => {
    const field = await fields.create({
      schema_id: id,
      custom_field: { ...data, required: false },
    })
    resourceFields.fetch()

    return field
  }

  const updateCustomField = (field: Field | CustomField, data: any) =>
    field.save({ ...data, schema_id: id, required: false })

  const destroyCustomField = (field: Field | CustomField) => {
    return field.destroy({
      data: {
        schema_id: id,
      },
    })
  }

  if (loading) return <Loading />

  return (
    <Panes direction="vertical">
      <Details schema={schema} updateSchema={updateSchema} />

      <CustomFields
        customFields={fields.models}
        resourceFields={resourceFields.models}
        createCustomField={createCustomField}
        updateCustomField={updateCustomField}
        destroyCustomField={destroyCustomField}
        illustration="list-document"
        modalIllustration="list-document"
        title={i18n.t('settings.custom_table.custom_fields.title')}
        description={i18n.t('settings.custom_table.custom_fields.description')}
        descriptionLink={DescriptionLink}
        modalTitle={i18n.t('settings.custom_table.custom_fields.modal.title')}
        modalDescription={i18n.t('settings.custom_table.custom_fields.modal.description')}
        modalLabel={i18n.t('settings.custom_table.custom_fields.modal.label')}
        modalButton={i18n.t('settings.custom_table.custom_fields.modal.button')}
        modalAlert={i18n.t('settings.custom_table.custom_fields.modal.alert')}
        hideCustomResourceAssignment
        showAllowedFieldTypes
        allowedFieldTypes={[
          FieldTypes.text,
          FieldTypes.longText,
          FieldTypes.date,
          FieldTypes.cents,
          FieldTypes.singleChoice,
          FieldTypes.rating,
        ]}
        showRequired={false}
        canCreateCustomField={canCreate && canManageResourceCustomFields}
        canEditCustomField={canEdit && canManageResourceCustomFields}
        canDeleteCustomField={canDelete && canManageResourceCustomFields}
      />
    </Panes>
  )
}

export default observer(Content)
